import { useMemo, useState, type JSX } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { JizdaListItem } from '@/api/types/Jizda';
import { formatDateCs, formatWeekdayCs, todayIsoDate } from '@/utils/date';

import { CalendarDayColumn } from './CalendarDayColumn';
import { indexByDay } from './grouping';

export interface WeekCalendarMobileProps {
  weekDays: string[];
  items: JizdaListItem[];
  onOpen: (id: string) => void;
}

// Mobile weekly view (CHR-03 §6 / ADR-0051 / ADR-0029): a DISTINCT day-stack
// tree — one collapsible accordion per day Po–Ne — NOT a CSS-collapsed version
// of the desktop 7-column grid. Each day's summary shows the weekday + date +
// a fůra count; expanding reveals the same commodity-grouped body
// (`CalendarDayColumn`) used on desktop. Today's day starts expanded.
export const WeekCalendarMobile = ({
  weekDays,
  items,
  onOpen,
}: WeekCalendarMobileProps): JSX.Element => {
  const theme = useTheme();
  const today = todayIsoDate();

  const byDay = useMemo(() => indexByDay(items), [items]);

  // Track which day panels are open; default-open today's. Keyed by ISO date.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    today ? { [today]: true } : {},
  );

  const toggle = (iso: string): void =>
    setExpanded((prev) => ({ ...prev, [iso]: !prev[iso] }));

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      data-testid="week-calendar-mobile"
    >
      <Stack gap={theme.spacing(1)}>
        {weekDays.map((iso) => {
          const dayItems = byDay.get(iso) ?? [];
          const isToday = iso === today;
          const isOpen = expanded[iso] ?? false;
          return (
            <Accordion
              key={iso}
              expanded={isOpen}
              onChange={() => toggle(iso)}
              disableGutters
              data-testid={`calendar-day-${iso}`}
              data-today={isToday ? 'true' : 'false'}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                boxShadow: 'none',
                '&:before': { display: 'none' },
                ...(isToday
                  ? { borderColor: theme.palette.primary.main }
                  : null),
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 56,
                  bgcolor: isToday
                    ? theme.palette.primary.light
                    : theme.palette.background.default,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={theme.spacing(1)}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: isToday
                        ? theme.palette.primary.dark
                        : theme.palette.text.primary,
                    }}
                  >
                    {formatWeekdayCs(iso)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: theme.monospaceFontFamily,
                      fontFeatureSettings: '"tnum"',
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {formatDateCs(iso)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {dayItems.length > 0 ? (
                    <Chip
                      size="small"
                      label={dayItems.length}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    />
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: theme.spacing(1.5) }}>
                <CalendarDayColumn items={dayItems} onOpen={onOpen} />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
};
